<template>
  <div class="container-page">
    <div class="monitoring-container">
      <!-- title -->
      <p class="title-inner">Мониторинг реализации поручений ЗА</p>
      <!-- filter analysis -->
      <div class="monitoring-content__header">
        <div class="filter greenBlock">
          <div class="filter__block">
            <p class="label">Страна</p>
            <div class="input-container">
              <SelectMultiComponent
                placeholder="Страна"
                mode="multiple"
                :hideSelected="false"
                :options="countryList"
                v-model="filter.countryIds"
                :disabled="!countryListLoaded"
                :async="false"
                :canClear="true"
              />
            </div>
          </div>

          <div class="filter__block">
            <p class="label">Выберите период</p>
            <div class="input-container flex">
              <FilterInput
                :typeable="true"
                type="date"
                v-model="filter.createdFrom"
                v-on:valueChangedDebounced="changeFilter"
              />
              <FilterInput
                :typeable="true"
                type="date"
                v-model="filter.createdTo"
                v-on:valueChangedDebounced="changeFilter"
              />
            </div>
          </div>

          <div class="filter__block">
            <p class="label">Мероприятия МПК</p>
            <div class="input-container">
              <SelectMultiComponent
                placeholder="Мероприятие"
                mode="multiple"
                :hideSelected="false"
                :options="filteredEventList"
                v-model="filter.eventIds"
                :disabled="!eventListLoaded"
                :async="false"
                :canClear="true"
              />
            </div>
          </div>
        </div>
        <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
      </div>

      <div class="widget-box">
        <!-- Внутренности виджета любые -->
        <div class="widget-inner">
          <div class="monitoring-content__header">
            <div class="analysis">
              <p>Всего поручений: {{ totalData.totalCount }}</p>
              <p>Просрочено: {{ totalData.overdue }}</p>
              <p>В работе: {{ totalData.inWork }}</p>
              <p>Выполнено: {{ totalData.done }}</p>
            </div>
          </div>

          <!-- charts -->
          <div v-if="initialized" class="monitoring-content__chart">
            <!-- charts -->
            <ChartBarMonitoring :seriesProp="dataMonitoring" :isLoading="isLoadingChart" />
          </div>

          <!-- btns & indicators -->
          <div class="monitoring-content__footer">
            <ButtonComponent mod="gradient-bg" @click="goto">Подробная информация</ButtonComponent>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';

  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import FilterInput from '@/common/components/FilterInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ChartBarMonitoring from '@/components/ChartComponents/ChartBarMonitoring.vue';

  import API from '../api/assignment';

  const PERMISSIONS = {
    view: Constants.Permissions.EapAssignmentAccess,
  };

  export default {
    name: 'EapAssignmentMonitoring',
    components: {
      ButtonComponent,
      ChartBarMonitoring,
      FilterInput,
      SelectMultiComponent,
      DefaultFilter,
    },
    watch: {
      'filter.countryIds': {
        handler() {
          if (this.initialized) {
            this.updateFilteredEvents();
            this.changeFilter();
          }
        },
      },
      'filter.eventIds': function () {
        if (this.initialized) {
          this.changeFilter();
        }
      },
    },
    mixins: [security],
    data() {
      return {
        filter: {
          countryIds: [],
          eventIds: [],
          createdFrom: null,
          createdTo: null,
        },
        countryList: [],
        allEvents: [],
        filteredEventList: [],
        countryListLoaded: false,
        eventListLoaded: false,
        initialized: false,

        totalData: {
          totalCount: 0,
        },
        dataMonitoring: [],
        isLoadingChart: false,
        chartAbortController: null,
      };
    },
    async created() {
      const isAllowed = await this.checkPermissionAndRedirect(PERMISSIONS.view);
      if (!isAllowed) return;

      try {
        const savedFilter = await loadFilter(this.$options.name);

        await Promise.all([this.loadCountries(), this.loadAllEvents()]);

        if (savedFilter) {
          this.filter = savedFilter;
        }

        const countryIds = this.$route.query?.countryIds ?? [];
        this.filter.countryIds = countryIds;
        this.$router.replace({
          name: 'EapAssignmentMonitoring',
        });

        this.initialized = true;
        this.updateFilteredEvents();
        this.loadPage();
      } catch {
        this.initialized = true;
      }
    },
    methods: {
      changeFilter() {
        saveFilter(this.$options.name, this.filter);
        this.loadPage();
      },

      async loadCountries() {
        try {
          const response = await Utils.searchNsi('countryList', {});
          if (response.data) {
            this.countryList = response.data.map((i) => ({ value: i.id, label: i.text }));
          }
        } finally {
          this.countryListLoaded = true;
        }
      },

      async loadAllEvents() {
        try {
          const response = await API.getEventForList();

          this.allEvents = response.data
            .filter((x) => x.eventType == 0)
            .map((i) => ({
              value: i.eventId,
              label: i.eventName,
              countryId: i.countryId,
            }));

          this.filteredEventList = [...this.allEvents];
        } catch {
          this.allEvents = [];
          this.filteredEventList = [];
        } finally {
          this.eventListLoaded = true;
        }
      },

      updateFilteredEvents() {
        if (this.filter.countryIds.length === 0) {
          this.filteredEventList = [...this.allEvents];
        } else {
          this.filteredEventList = this.allEvents.filter((event) =>
            this.filter.countryIds.includes(event.countryId.toString()),
          );
        }

        this.filter.eventIds = this.filter.eventIds.filter((eventId) =>
          this.filteredEventList.some((event) => event.value === eventId),
        );
      },

      loadPage() {
        if (!this.initialized) return;

        this.chartAbortController?.abort();
        this.chartAbortController = new AbortController();
        const { signal } = this.chartAbortController;

        this.isLoadingChart = true;
        this.dataMonitoring = [];

        API.getTotal({ ...this.filter }, { signal })
          .then((response) => {
            this.totalData = {};
            if (response.data && response.data.length > 0) {
              this.totalData = response.data[0];
              response.data.slice(1).forEach((el) => {
                this.dataMonitoring.push({
                  name: el.title,
                  data: [el.overdue, el.inWork, el.done],
                });
              });
            }
          })
          .catch((error) => {
            if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
          })
          .finally(() => {
            if (signal.aborted) return;
            this.isLoadingChart = false;
          });
      },

      goto() {
        const filterParams = {
          countryIds: this.filter.countryIds,
          createdFrom: this.filter.createdFrom,
          createdTo: this.filter.createdTo,
        };

        this.$router.push({
          name: 'EapAssignments',
          query: filterParams,
        });
      },

      onLoadFilter(item) {
        this.filter = item;
        this.updateFilteredEvents();
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss">
  .monitoring {
    &-content {
      &__header {
        .filter {
          &.greenBlock {
            .filter__block {
              .input-container {
                .multiselect {
                  background: $green;
                  border: none;
                  &.is-open {
                    .multiselect-placeholder {
                      display: none;
                    }
                  }
                  .multiselect-search {
                    color: white;
                    -webkit-text-fill-color: white;
                  }
                  input {
                    background-color: $green;
                  }
                }
                .select2 .select2-container--default .select2-selection--single {
                  background-color: $green;
                  border: none;
                }
                .select2 .select2-container--default .select2-selection--single .select2-selection__placeholder,
                .multiselect-placeholder,
                .select2 .select2-container .select2-selection__rendered,
                .multiselect-multiple-label,
                .multiselect-placeholder,
                .multiselect-single-label {
                  font-weight: 400;
                  color: white;
                }
                .select2 .select2-container .select2-selection__rendered {
                  line-height: 44px;
                }
                .select2 .select2-container--default .select2-selection--single .select2-selection__arrow b,
                .multiselect-caret {
                  border-color: white !important;
                }
                .multiselect-clear-icon {
                  background-color: white;
                  margin-right: 6px;
                }
              }
            }
          }
        }
      }
    }
  }
</style>

<style lang="scss" scoped>
  .monitoring {
    &-container {
      &:not(:last-of-type) {
        margin-bottom: 130px;
      }
      .title-inner {
        font-weight: 500;
      }
    }
    &-content {
      &__header {
        .analysis {
          margin-bottom: 0;
          padding: 12px 18px;
        }
      }
    }
  }
  .monitoring-content__footer button {
    margin: 16px;
  }
</style>
